import React from 'react';
import { AppBar, Box, Container, Toolbar, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useViewMode } from '../../contexts/ViewModeContext';

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Main layout component with dual UI layout support:
 * - WebView UI: Minimal layout (no AppBar, no Footer, transparent background) for mobile app InAppBrowser
 * - Browser UI: Full layout with AppBar and Footer for standalone web access
 *
 * UI mode is determined by IsWebView cookie (set when ?is_webview=true query parameter is present)
 */
export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const { isWebView } = useViewMode();

  // WebView UI: Minimal layout for in-app browser (no AppBar, no Footer)
  if (isWebView) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {children}
      </Box>
    );
  }

  // Browser UI: Full layout with navigation and branding
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {t('common.appName')}
          </Typography>
        </Toolbar>
      </AppBar>
      <Container component="main" maxWidth="lg" sx={{ flexGrow: 1, py: 4 }}>
        {children}
      </Container>
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : theme.palette.grey[100],
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} Vreeda GmbH
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
